// src/modules/app-views/products/pages/Ai/components/SearchedProduct.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Typography, Divider, Descriptions, Collapse, Grid, theme } from 'antd';
import {
  EditOutlined,
  InfoCircleOutlined,
  TagsOutlined,
  SettingOutlined,
  AuditOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import TypingText from './TypingText';
import ProductImageWithFallback from './ProductImageWithFallback';
import { formatPrice, buildNaturalIntro } from '../helpers';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SearchedProduct - Displays the AI cataloged product details
 */
const SearchedProduct = ({
  message,
  typingProgress,
  introTypingComplete,
  catalogedProductId,
  chatContainerRef,
  showAvatar = true,
  creditsInfo = {},
  onIntroComplete,
  onEditProduct,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Detect mobile (breakpoint md = 768px) - same as SearchedEntity.jsx
  const isMobile = !screens.md;

  const { product, meta, creditsDebit, resolvedSupplier, alreadyExists } = message;
  const tp = typingProgress; // shorthand for typing progress
  const tc = product?.technicalCharacteristics;
  const fd = product?.fiscalData;
  const cl = product?.classification;

  // Generate natural language intro
  const naturalIntro = buildNaturalIntro(product, alreadyExists, resolvedSupplier);

  // Check if sections have data
  const hasProductData =
    product?._id ||
    product?.sku ||
    product?.ean ||
    product?.gtin ||
    product?.manufacturerCode ||
    product?.description;
  const hasClassification = cl && (cl.brand || cl.category || cl.subcategory || cl.manufacturer);
  const hasTechnicalData = tc && (tc.current || tc.voltage || tc.poles || tc.color || tc.material);
  const hasFiscalData = fd && (fd.ncm || fd.cfop);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      {/* Natural language intro with typing effect */}
      <div style={{ marginBottom: 20, lineHeight: 1.6, fontSize: 15 }}>
        <TypingText
          text={naturalIntro}
          speed={10}
          onComplete={onIntroComplete}
          scrollContainerRef={chatContainerRef}
          style={{ color: 'var(--ant-color-text)' }}
        />
      </div>

      {/* Product Header: Image + Title/Subtitle - shows after intro */}
      {introTypingComplete &&
        tp.image &&
        (isMobile ? (
          // Mobile: vertical layout, centered
          <Flex vertical align="center" style={{ marginBottom: 20 }} className="fade-in">
            {/* Product image - centered (conditional based on config) */}
            {showAvatar && (
              <ProductImageWithFallback
                avatar={
                  product?.avatar ||
                  product?.attachments?.find(a => a.attachmentType === 'avatar') ||
                  product?.attachments?.[0] ||
                  product?.images?.[0]
                }
                name={product?.shortDescription || product?.name}
                backColor={product?.backColor}
                foreColor={product?.foreColor}
              />
            )}

            {/* Title - centered and truncated */}
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 1.3,
                marginTop: 12,
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product?.shortDescription || product?.name}
            </div>

            {/* Subtitle - centered and truncated */}
            {product?.shortDescription && product?.name && (
              <Text
                type="secondary"
                style={{
                  fontSize: 13,
                  lineHeight: 1.4,
                  marginTop: 4,
                  textAlign: 'center',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {product.name}
              </Text>
            )}

            {/* Price - centered */}
            <Flex align="center" justify="center" gap={8} style={{ marginTop: 12, width: '100%' }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: token.colorPrimary,
                }}
              >
                {formatPrice(product?.price, true)}
              </div>

              {!alreadyExists && (
                <Tag color="blue" style={{ margin: 0 }}>
                  {translateX('suggested_price')}
                </Tag>
              )}
            </Flex>
          </Flex>
        ) : (
          // Desktop: horizontal layout
          <Flex gap={16} align="flex-start" style={{ marginBottom: 20 }} className="fade-in">
            {/* Product image - fixed 150x150 (conditional based on config) */}
            {showAvatar && (
              <ProductImageWithFallback
                avatar={
                  product?.avatar ||
                  product?.attachments?.find(a => a.attachmentType === 'avatar') ||
                  product?.attachments?.[0] ||
                  product?.images?.[0]
                }
                name={product?.shortDescription || product?.name}
                backColor={product?.backColor}
                foreColor={product?.foreColor}
              />
            )}

            {/* Title and subtitle */}
            <Flex vertical justify="center" style={{ minHeight: 150 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  marginBottom: 8,
                }}
              >
                {product?.shortDescription || product?.name}
              </div>
              {product?.shortDescription && product?.name && (
                <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.4 }}>
                  {product.name}
                </Text>
              )}
              {/* Price highlight - always show, R$ 0,00 if empty */}
              <Flex align="center" gap={8} style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: token.colorPrimary,
                  }}
                >
                  {formatPrice(product?.price, true)}
                </div>

                {!alreadyExists && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    {translateX('suggested_price')}
                  </Tag>
                )}
              </Flex>
            </Flex>
          </Flex>
        ))}

      {/* Product Data Section */}
      {introTypingComplete && tp.name && hasProductData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['product-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'product-data',
              label: (
                <Flex align="center" gap={8}>
                  <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('product_data')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {product?._id && <Descriptions.Item label="ID">{product._id}</Descriptions.Item>}
                  {product?.sku && <Descriptions.Item label="SKU">{product.sku}</Descriptions.Item>}
                  {product?.ean && <Descriptions.Item label="EAN">{product.ean}</Descriptions.Item>}
                  {product?.gtin && (
                    <Descriptions.Item label="GTIN">{product.gtin}</Descriptions.Item>
                  )}
                  {product?.manufacturerCode && (
                    <Descriptions.Item label={translateX('manufacturer_code')}>
                      {product.manufacturerCode}
                    </Descriptions.Item>
                  )}
                  {product?.description && (
                    <Descriptions.Item label={translateX('description')}>
                      {product.description}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Classification Section */}
      {introTypingComplete && tp.category && hasClassification && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['classification']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'classification',
              label: (
                <Flex align="center" gap={8}>
                  <TagsOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('classification')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {cl.brand && (
                    <Descriptions.Item label={translateX('brand')}>{cl.brand}</Descriptions.Item>
                  )}
                  {cl.manufacturer && (
                    <Descriptions.Item label={translateX('manufacturer')}>
                      {cl.manufacturer}
                    </Descriptions.Item>
                  )}
                  {cl.category && (
                    <Descriptions.Item label={translateX('category')}>
                      {cl.category}
                    </Descriptions.Item>
                  )}
                  {cl.subcategory && (
                    <Descriptions.Item label={translateX('subcategory')}>
                      {cl.subcategory}
                    </Descriptions.Item>
                  )}
                  {cl.type && (
                    <Descriptions.Item label={translateX('type')}>{cl.type}</Descriptions.Item>
                  )}
                  {cl.line && (
                    <Descriptions.Item label={translateX('line')}>{cl.line}</Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Technical Characteristics Section */}
      {introTypingComplete && tp.ncm && hasTechnicalData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['technical']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'technical',
              label: (
                <Flex align="center" gap={8}>
                  <SettingOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('technical_characteristics')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {tc.poles && (
                    <Descriptions.Item label={translateX('poles')}>{tc.poles}</Descriptions.Item>
                  )}
                  {tc.current && (
                    <Descriptions.Item label={translateX('current')}>
                      {tc.current}
                    </Descriptions.Item>
                  )}
                  {tc.voltage && (
                    <Descriptions.Item label={translateX('voltage')}>
                      {tc.voltage}
                    </Descriptions.Item>
                  )}
                  {tc.power && (
                    <Descriptions.Item label={translateX('power')}>{tc.power}</Descriptions.Item>
                  )}
                  {tc.frequency && (
                    <Descriptions.Item label={translateX('frequency')}>
                      {tc.frequency}
                    </Descriptions.Item>
                  )}
                  {tc.color && (
                    <Descriptions.Item label={translateX('color')}>{tc.color}</Descriptions.Item>
                  )}
                  {tc.material && (
                    <Descriptions.Item label={translateX('material')}>
                      {tc.material}
                    </Descriptions.Item>
                  )}
                  {tc.format && (
                    <Descriptions.Item label={translateX('format')}>{tc.format}</Descriptions.Item>
                  )}
                  {tc.installationType && (
                    <Descriptions.Item label={translateX('installation_type')}>
                      {tc.installationType}
                    </Descriptions.Item>
                  )}
                  {tc.protectionDegree && (
                    <Descriptions.Item label={translateX('protection_degree')}>
                      {tc.protectionDegree}
                    </Descriptions.Item>
                  )}
                  {tc.standard && (
                    <Descriptions.Item label={translateX('standard')}>
                      {tc.standard}
                    </Descriptions.Item>
                  )}
                  {tc.certification && (
                    <Descriptions.Item label={translateX('certification')}>
                      {tc.certification}
                    </Descriptions.Item>
                  )}
                  {tc.application && (
                    <Descriptions.Item label={translateX('application')}>
                      {tc.application}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Fiscal Data Section */}
      {introTypingComplete && tp.price && hasFiscalData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['fiscal-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'fiscal-data',
              label: (
                <Flex align="center" gap={8}>
                  <AuditOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('fiscal_data')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {fd.ncm && <Descriptions.Item label="NCM">{fd.ncm}</Descriptions.Item>}
                  {fd.cfop && <Descriptions.Item label="CFOP">{fd.cfop}</Descriptions.Item>}
                  {fd.cest && <Descriptions.Item label="CEST">{fd.cest}</Descriptions.Item>}
                  {fd.icms?.origin !== undefined && (
                    <Descriptions.Item label={translateX('origin')}>
                      {fd.icms.origin}
                    </Descriptions.Item>
                  )}
                  {fd.icms?.csosn && (
                    <Descriptions.Item label="CSOSN">{fd.icms.csosn}</Descriptions.Item>
                  )}
                  {fd.icms?.cst && (
                    <Descriptions.Item label="CST ICMS">{fd.icms.cst}</Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Supplier Section */}
      {introTypingComplete && tp.supplier && resolvedSupplier && (
        <Collapse
          className="fade-in collapsed-content-fit"
          collapsible="icon"
          activeKey={['supplier']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'supplier',
              label: (
                <Flex align="center" gap={8}>
                  <ShopOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('supplier')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  <Descriptions.Item label={translateX('name')}>
                    <Flex gap={8} align="center">
                      {resolvedSupplier.name}
                      {resolvedSupplier.isNew && <Tag color="green">{translateX('new')}</Tag>}
                    </Flex>
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Meta info and Edit link */}
      {introTypingComplete && tp.meta && meta && (
        <div className="fade-in" style={{ marginTop: 16 }}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            wrap="wrap"
            gap={isMobile ? 12 : 16}
          >
            {/* Meta stats */}
            <Flex gap={isMobile ? 16 : 24} wrap="wrap" justify={isMobile ? 'center' : 'flex-start'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('model')}:</span> {meta.model}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>Tokens:</span>{' '}
                {meta.totalTokens?.toLocaleString()}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('time')}:</span>{' '}
                {(meta.responseTimeMs / 1000).toFixed(1)}s
              </Text>
              {creditsDebit?.debited !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('credits_debited')}:</span>{' '}
                  {creditsDebit.debited}
                </Text>
              )}
              {creditsInfo?.availableCredits !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('current_balance')}:</span>{' '}
                  {creditsInfo.availableCredits}
                </Text>
              )}
            </Flex>

            {/* Edit Product link */}
            {tp.editLink && catalogedProductId && (
              <Flex gap={16} align="center" justify={isMobile ? 'center' : 'flex-end'}>
                <a
                  onClick={() => onEditProduct(catalogedProductId)}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: isMobile ? 16 : 0,
                  }}
                  className="fade-in"
                >
                  <EditOutlined />
                  {translateX('edit_product')}
                </a>
              </Flex>
            )}
          </Flex>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SearchedProduct;
